import React,{useMemo,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AvailabilityEditor from './AvailabilityEditor';
import './event-calendar.css';

const {
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiMapPin,
  FiPlus,
  FiX
}=FiIcons;

const monthNames=[
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const weekDays=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const availabilityKey='amp-dj-availability';

function parseEventDate(value) {
  const iso=String(value||'').match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if(iso){
    const date=new Date(Number(iso[1]),Number(iso[2])-1,Number(iso[3]));
    return Number.isNaN(date.getTime())?null:date;
  }
  const parsed=new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value) {
  const date=parseEventDate(value);
  return date?`${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}-${String(date.getFullYear()).slice(-2)}`:value||'—';
}

function dateKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function readAvailability() {
  try {
    return JSON.parse(localStorage.getItem(availabilityKey) || '{}');
  } catch {
    return {};
  }
}

function EventCalendar({events}) {
  const sortedEvents=[...events].sort((first,second)=>
    (parseEventDate(first.date)?.getTime()??Number.MAX_SAFE_INTEGER)-
    (parseEventDate(second.date)?.getTime()??Number.MAX_SAFE_INTEGER)
  );
  const firstEventDate=parseEventDate(sortedEvents[0]?.date) || new Date();
  const [month,setMonth]=useState(
    new Date(firstEventDate.getFullYear(),firstEventDate.getMonth(),1)
  );
  const [selectedEvent,setSelectedEvent]=useState(null);
  const [showSchedule,setShowSchedule]=useState(false);
  const [schedule,setSchedule]=useState({
    eventId:'',
    time:'',
    location:'',
    notes:''
  });
  const [selectedDate,setSelectedDate]=useState('');
  const [selectedDj,setSelectedDj]=useState('Asi Vidal');
  const [availability,setAvailability]=useState(readAvailability);

  const days=useMemo(()=> {
    const start=new Date(month.getFullYear(),month.getMonth(),1);
    const end=new Date(month.getFullYear(),month.getMonth()+1,0);
    const cells=[];

    for (let index=0;index<start.getDay();index+=1) cells.push(null);
    for (let day=1;day<=end.getDate();day+=1) {
      cells.push(new Date(month.getFullYear(),month.getMonth(),day));
    }
    while (cells.length % 7!==0) cells.push(null);
    return cells;
  },[month]);

  const eventsByDate=useMemo(
    ()=> sortedEvents.reduce((groups,event)=> {
      const date=parseEventDate(event.date);
      if (!date) return groups;
      const key=dateKey(date);
      groups[key]=[...(groups[key] || []),event];
      return groups;
    },{}),
    [events]
  );

  const selectedAvailability=availability[selectedDj]?.[selectedDate] || {
    status:'available',
    note:''
  };

  const moveMonth=(amount)=> setMonth((current)=> new Date(
    current.getFullYear(),
    current.getMonth()+amount,
    1
  ));

  const selectDate=(date)=> {
    if (!date) return;
    setSelectedDate(dateKey(date));
  };

  const updateAvailability=(key,value)=> {
    const next={
      ...availability,
      [selectedDj]: {
        ...(availability[selectedDj] || {}),
        [selectedDate]: {
          ...selectedAvailability,
          [key]: value
        }
      }
    };

    setAvailability(next);
    localStorage.setItem(availabilityKey,JSON.stringify(next));
  };

  const openSchedule=(event)=> {
    setSelectedEvent(event);
    setSchedule({
      eventId:event.id,
      time:event.scheduleTime || '',
      location:event.venue || '',
      notes:event.scheduleNotes || ''
    });
    setShowSchedule(true);
  };

  const saveSchedule=(event)=> {
    event.preventDefault();
    setShowSchedule(false);
  };

  return (
    <main className="main-content calendar-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Production scheduling</span>
          <h1>DJ availability calendar</h1>
          <p>See event bookings and keep the production team’s availability up to date.</p>
        </div>

        <button
          type="button"
          className="button primary"
          onClick={()=> setShowSchedule(true)}
        >
          <SafeIcon icon={FiPlus} />
          Schedule detail
        </button>
      </div>

      <section className="calendar-layout">
        <div className="panel calendar-panel">
          <div className="calendar-toolbar">
            <div>
              <h2>{monthNames[month.getMonth()]} {month.getFullYear()}</h2>
              <span>{sortedEvents.length} upcoming events</span>
            </div>

            <div className="calendar-nav">
              <button
                className="icon-btn"
                onClick={()=> moveMonth(-1)}
                aria-label="Previous month"
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <button
                className="button secondary"
                onClick={()=> setMonth(new Date(
                  firstEventDate.getFullYear(),
                  firstEventDate.getMonth(),
                  1
                ))}
              >
                Next event
              </button>
              <button
                className="icon-btn"
                onClick={()=> moveMonth(1)}
                aria-label="Next month"
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>

          <div className="availability-legend">
            <span><i className="availability-dot available" /> Available</span>
            <span><i className="availability-dot limited" /> Limited</span>
            <span><i className="availability-dot unavailable" /> Unavailable</span>
          </div>

          <div className="calendar-grid calendar-weekdays">
            {weekDays.map((day)=><span key={day}>{day}</span>)}
          </div>

          <div className="calendar-grid calendar-days">
            {days.map((day,index)=> {
              const key=day ? dateKey(day) : '';
              const dayEvents=day ? eventsByDate[key] || [] : [];
              const status=day ? availability[selectedDj]?.[key]?.status : '';
              const isSelected=key===selectedDate;

              return (
                <div
                  className={`calendar-cell ${day ? '' : 'empty'} ${isSelected ? 'selected-day' : ''}`}
                  key={`${day?.toISOString() || 'empty'}-${index}`}
                  onClick={()=> selectDate(day)}
                >
                  {day && (
                    <button
                      type="button"
                      className="calendar-day-number"
                      onClick={(event)=> {
                        event.stopPropagation();
                        selectDate(day);
                      }}
                    >
                      {day.getDate()}
                    </button>
                  )}

                  {status && (
                    <i className={`availability-dot ${status}`} title={status} />
                  )}

                  <div className="calendar-events">
                    {dayEvents.map((event)=> (
                      <button
                        key={event.id}
                        className="calendar-event"
                        onClick={(clickEvent)=> {
                          clickEvent.stopPropagation();
                          openSchedule(event);
                        }}
                      >
                        <b>{event.name}</b>
                        <small>{event.type}</small>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="calendar-sidebar">
          <AvailabilityEditor
            selectedDate={selectedDate}
            selectedDj={selectedDj}
            value={selectedAvailability.status}
            note={selectedAvailability.note}
            onDjChange={setSelectedDj}
            onStatusChange={(value)=> updateAvailability('status',value)}
            onNoteChange={(value)=> updateAvailability('note',value)}
          />

          <section className="panel agenda-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">At a glance</span>
                <h2>Upcoming events</h2>
              </div>
            </div>

            <div className="agenda-list">
              {sortedEvents.map((event)=> {
                const date=parseEventDate(event.date);
                return (
                  <button
                    className="agenda-item"
                    key={event.id}
                    onClick={()=> openSchedule(event)}
                  >
                    <span className="agenda-date">
                      <b>{date?.getDate() || '—'}</b>
                      <small>
                        {date ? monthNames[date.getMonth()].slice(0,3) : 'TBD'}
                      </small>
                    </span>
                    <span>
                      <strong>{event.name}</strong>
                      <small>
                        <SafeIcon icon={FiMapPin} /> {event.venue} · {formatDate(event.date)}
                      </small>
                    </span>
                    <SafeIcon icon={FiChevronRight} />
                  </button>
                );
              })}
            </div>
          </section>
        </aside>
      </section>

      {showSchedule && (
        <div className="modal-backdrop">
          <form className="modal-card schedule-modal" onSubmit={saveSchedule}>
            <div className="modal-heading">
              <div>
                <span className="eyebrow">Production timing</span>
                <h2>{selectedEvent ? selectedEvent.name : 'Schedule event detail'}</h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={()=> setShowSchedule(false)}
                aria-label="Close schedule detail"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {!selectedEvent && (
              <label className="schedule-field">
                <span>Event</span>
                <select
                  value={schedule.eventId}
                  onChange={(event)=> setSchedule({
                    ...schedule,
                    eventId:event.target.value
                  })}
                  required
                >
                  <option value="">Select an event</option>
                  {sortedEvents.map((event)=> (
                    <option value={event.id} key={event.id}>{event.name}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="schedule-field">
              <span><SafeIcon icon={FiClock} /> Setup or call time</span>
              <input
                type="time"
                value={schedule.time}
                onChange={(event)=> setSchedule({
                  ...schedule,
                  time:event.target.value
                })}
              />
            </label>

            <label className="schedule-field">
              <span><SafeIcon icon={FiMapPin} /> Production location</span>
              <input
                value={schedule.location}
                onChange={(event)=> setSchedule({
                  ...schedule,
                  location:event.target.value
                })}
                placeholder="Venue or load-in location"
              />
            </label>

            <label className="schedule-field">
              <span>Scheduling notes</span>
              <textarea
                value={schedule.notes}
                onChange={(event)=> setSchedule({
                  ...schedule,
                  notes:event.target.value
                })}
                placeholder="Add setup, load-in or vendor notes..."
              />
            </label>

            <div className="modal-actions">
              <button
                type="button"
                className="button secondary"
                onClick={()=> setShowSchedule(false)}
              >
                Cancel
              </button>
              <button className="button primary">Save schedule</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

export default EventCalendar;